// AF-3.16 — Brand Growth OS design system: status/governance/readiness/
// approval/confidence badges.
//
// CRITICAL INVARIANT: every badge renders the backend's own enum value
// VERBATIM as its label. These components never invent a value, never
// rename a value, and never collapse UNKNOWN/INSUFFICIENT_EVIDENCE into
// a positive-looking state. Only the COLOR is inferred (semantic, not
// data), and the inference is deliberately conservative: anything not
// explicitly known-positive renders neutral/gray, never green.

import { cn } from "@/lib/utils";

const POSITIVE = new Set([
  "HEALTHY", "READY", "COMPLIANT", "APPROVED", "POSITIVE", "IMPROVING",
  "ON_TRACK", "COVERED", "STRONG", "ACTIVE", "GENERATED", "SELECT_PRIMARY",
  "ALLOWS", "CRAWLABLE", "INDEXABLE", "AI_ACCESSIBLE",
  // AF-3.18: a connector genuinely CONNECTED or a sync that genuinely
  // SUCCEEDED are real positive states -- unlike "Healthy" merely from
  // OAuth succeeding (which this codebase never renders at all; see
  // DataConnectionsPage.js), these come from an actual completed
  // read-only data operation.
  "CONNECTED", "SUCCESS",
]);
const CAUTION = new Set([
  "MEASURING", "LEARNING", "OPPORTUNITY_RICH", "PARTIALLY_READY", "CAUTION",
  "PARTIALLY_COVERED", "PARTIAL", "PENDING", "NEUTRAL", "SELECT_ALTERNATE",
  "IN_PROGRESS", "WAITING_APPROVAL", "DRAFT", "NEXT", "WAITING", "AGING",
  "REQUIRES", "PARTIALLY_CRAWLABLE",
  // AF-3.18
  "SYNCING", "NEVER_SYNCED",
]);
const NEGATIVE = new Set([
  "AT_RISK", "BLOCKED", "RESTRICTED", "PROHIBITED", "REJECTED", "NEGATIVE",
  "OFF_TRACK", "FAILED", "CANCELLED", "NOT_READY", "REVISE", "HOLD",
  "UNCOVERED", "CONFLICTING", "STALE", "DETERIORATING",
  "RESTRICTS", "PROHIBITS", "NOT_INDEXABLE", "AI_RESTRICTED",
  // AF-3.18
  "ERROR", "DISCONNECTED", "CREDENTIAL_REVOKED",
]);
const NEUTRAL = new Set([
  "UNKNOWN", "INSUFFICIENT_EVIDENCE", "INSUFFICIENT_DATA", "NO_TARGET",
  "NOT_PROVEN", "NOT_PROVIDED", "INCONCLUSIVE", "ENVIRONMENT_CHANGE",
  "NONE", "NOT_APPLICABLE", "NOT_AVAILABLE",
]);

function toneFor(value) {
  const v = String(value || "").toUpperCase();
  if (POSITIVE.has(v)) return "positive";
  if (CAUTION.has(v)) return "caution";
  if (NEGATIVE.has(v)) return "negative";
  if (NEUTRAL.has(v)) return "neutral";
  return "neutral"; // unknown-to-us value: never default to positive
}

// Day 2.5 -- "Martian Soft Intelligence" visual reskin. VISUAL ONLY:
// shape (pill instead of rounded-md) and the shared type-scale token
// replace the old hardcoded 10px size; every bg/text/border color token
// is byte-for-byte unchanged, so every existing contrast measurement in
// index.css's own accessibility audit still holds exactly as before.
// toneFor() and the POSITIVE/CAUTION/NEGATIVE/NEUTRAL sets above this
// function are untouched -- this reskin never changes which tone a
// backend value maps to, only how that tone is painted.
const TONE_STYLES = {
  positive: "bg-[var(--success-bg)] text-[var(--success)] border-[var(--success-border)]",
  caution: "bg-[var(--warning-bg)] text-[var(--warning)] border-[var(--warning-border)]",
  negative: "bg-[var(--error-bg)] text-[var(--error)] border-[var(--error-border)]",
  neutral: "bg-[var(--neutral-bg)] text-[var(--neutral-text)] border-[var(--neutral-border)]",
};

function BaseBadge({ value, testId, className, prefix }) {
  if (value === null || value === undefined || value === "") {
    return (
      <span data-testid={testId} className={cn("inline-flex items-center rounded-full border px-2.5 py-0.5 text-[var(--text-2xs)] font-mono uppercase tracking-wide", TONE_STYLES.neutral, className)}>
        NOT PROVIDED
      </span>
    );
  }
  const tone = toneFor(value);
  return (
    <span data-testid={testId} className={cn("inline-flex items-center rounded-full border px-2.5 py-0.5 text-[var(--text-2xs)] font-mono uppercase tracking-wide", TONE_STYLES[tone], className)}>
      {prefix ? `${prefix}: ` : ""}{String(value)}
    </span>
  );
}

// Generic backend-state badge (brand_state, control_status, growth_state,
// outcome_status, gap_status, primary_control_decision, etc.) -- pass the
// raw backend string as `value`.
export function StatusBadge({ value, className }) {
  return <BaseBadge value={value} testId="status-badge" className={className} />;
}

// AF-2.9 governance verdicts. UNKNOWN is rendered in the SAME neutral
// (never green) tone as everything else here, and callers are expected
// to pair it with the explicit "does not mean compliant" copy (see
// GovernanceNote below) wherever a governance verdict is surfaced.
export function GovernanceBadge({ value, className }) {
  return <BaseBadge value={value} testId="governance-badge" className={className} />;
}

export function GovernanceNote({ value }) {
  const v = String(value || "").toUpperCase();
  if (v === "UNKNOWN") {
    return <p className="text-[10px] text-[var(--text-tertiary)] mt-1">No sufficient governance evidence currently available. This does not mean compliant.</p>;
  }
  if (v === "RESTRICTED") {
    return <p className="text-[10px] text-[var(--text-tertiary)] mt-1">Community/platform policy restricts promotional behavior. Adapt strategy before distribution.</p>;
  }
  if (v === "PROHIBITED") {
    return <p className="text-[10px] text-[var(--text-tertiary)] mt-1">Distribution through this environment is prohibited.</p>;
  }
  if (v === "CAUTION") {
    return <p className="text-[10px] text-[var(--text-tertiary)] mt-1">Proceed with awareness of platform-specific constraints.</p>;
  }
  return null;
}

// ActionPlanService.READINESS_STATES / CreatorCommandService.readiness /
// BrandGrowthPlanService.planning_readiness -- always the raw backend value.
export function ReadinessBadge({ value, className }) {
  return <BaseBadge value={value} testId="readiness-badge" className={className} />;
}

// Production-asset approval_status (PENDING/APPROVED/REJECTED/NOT_REQUIRED)
// or a video/media generation_status.
export function ApprovalBadge({ value, className }) {
  return <BaseBadge value={value} testId="approval-badge" className={className} />;
}

// Confidence can be a backend ENUM (HIGH/MEDIUM/LOW/UNKNOWN, from AF-3.12/
// 3.13) or a numeric 0-0.95 value (AF-3.14). Never renders 100%/1.0 --
// AF-3.14's own confidence is hard-capped at 0.95 by the backend; this
// component does not add its own cap, it simply never fabricates a value
// beyond what the backend actually returned.
export function ConfidenceIndicator({ value, className }) {
  if (value === null || value === undefined) {
    return <BaseBadge value={null} testId="confidence-indicator" className={className} />;
  }
  if (typeof value === "number") {
    const pct = Math.round(value * 100);
    const tone = value >= 0.7 ? "positive" : value >= 0.4 ? "caution" : "negative";
    return (
      <div data-testid="confidence-indicator" className={cn("inline-flex items-center gap-1.5", className)}>
        <div className="w-16 h-1.5 rounded-full bg-[var(--neutral-bg)] overflow-hidden">
          <div className={cn("h-full rounded-full", tone === "positive" ? "bg-[var(--success)]" : tone === "caution" ? "bg-[var(--warning)]" : "bg-[var(--error)]")} style={{ width: `${pct}%` }} />
        </div>
        <span className="text-[10px] font-mono text-[var(--text-secondary)]">{pct}%</span>
      </div>
    );
  }
  return <BaseBadge value={value} testId="confidence-indicator" className={className} />;
}
